// =============================================================================
// GameOverView.ts — Fim de mandato reformulado (issues #91 e #96):
// - Quase-acerto VERDADEIRO: % real da apuração + quanto faltou, com a barra
//   quase cheia doendo na tela (nada de proximidade fabricada).
// - Botão REELEIÇÃO: rematch em 1 clique — a sala volta ao lobby com a mesma
//   chapa (o servidor faz o broadcast game:rematch que fecha esta tela).
// - Santinho compartilhável: card pixel art da run com título honorífico.
// Também mostra o crédito da meta-progressão (#90/#94), as Promessas de
// Campanha cumpridas na run (#99), as 3 pastas de dossiê (#92) e as armas
// supremas que a chapa alcançou via GOLPE DE MESTRE (#98).
// =============================================================================
import { GAME_DURATION } from '../../../shared/constants';
import type { GameOverPayload } from '../../../shared/protocol';
import { WEAPON_TABLE } from '../../../shared/weapons';
import { CONQUISTA_TABLE } from '../../../shared/feitos';
import { $, $in, charImg, type Overlay } from './dom';
import { renderDossies } from './dossies';
import { caixa2Icon, seloIcon } from './pixelIcons';
import { SantinhoPanel, honorifico } from './santinho';

export interface GameOverHandlers {
  onRematch: () => Promise<string | null>;  // null = pedido aceito; string = erro pro eleitor
  onExit: () => void;
}

const fmt = (secs: number): string =>
  `${Math.floor(secs / 60)}m${String(Math.floor(secs) % 60).padStart(2, '0')}s`;
const num = (n: number): string => n.toLocaleString('pt-BR');

export class GameOverView implements Overlay {
  // Raiz única do overlay: todos os ids desta view moram dentro do #gameover
  readonly root: HTMLElement = $('gameover');
  private readonly q = $in(this.root);
  private readonly santinho = new SantinhoPanel();

  show(data: GameOverPayload, youId: string | null, handlers: GameOverHandlers): void {
    this.q('go-title').textContent = data.victory ? 'VOCÊ SOBREVIVEU À APURAÇÃO' : 'FIM DE MANDATO';
    this.q('go-frase').textContent = `“${data.frase}”`;

    this.renderApuracao(data);

    const box = this.q('go-stats');
    box.innerHTML = `<div class="go-row"><span>Tempo de sobrevivência</span><b>${fmt(data.timeSurvived)}</b></div>`;
    if (data.evolutions?.length) {
      const supremas = data.evolutions.map((id) => WEAPON_TABLE[id]?.nome ?? id).join(' · ');
      box.innerHTML += `<div class="go-row evo-row"><span>Armas supremas da chapa</span><b>${supremas}</b></div>`;
    }
    for (const s of data.stats) {
      box.innerHTML += `
        <div class="go-row">
          <span>${charImg(s.charId, 30, false, s.skinId)} ${s.name} <i class="go-titulo">${honorifico(s, data.stats)}</i></span>
          <span><b>${num(s.score)}</b> pontos · nível <b>${s.level}</b> · <b>${s.kills}</b> adversários neutralizados</span>
        </div>`;
    }
    // Meta-progressão (#90/#94): a derrota também gera progresso — o resumo
    // mostra o que já foi creditado no perfil de cada um
    box.innerHTML += '<div class="go-meta-titulo">CREDITADO NO CURRAL ELEITORAL</div>';
    for (const s of data.stats) {
      box.innerHTML += `
        <div class="go-row go-meta">
          <span>${s.name}</span>
          <span>+<b>${num(s.votosGanhos ?? 0)}</b> votos vitalícios · ${caixa2Icon(13)} +<b>${num(s.caixa2Ganhos ?? 0)}</b> de Caixa 2</span>
        </div>`;
    }

    // Feitos da SUA participação (#99/#92): promessas cumpridas nesta run e
    // as pastas de dossiê já atualizadas com o progresso herdado
    const me = data.stats.find((s) => s.id === youId) ?? data.stats[0];
    const extra = this.q('go-feitos');
    extra.innerHTML = '';
    for (const id of me?.conquistas ?? []) {
      const c = CONQUISTA_TABLE[id];
      if (!c) continue;
      extra.innerHTML += `
        <div class="go-conquista">${seloIcon(18)}
          <span>PROMESSA CUMPRIDA: <b>${c.nome}</b> (+${num(c.votos)} votos)</span>
        </div>`;
    }
    if (me?.dossies?.length) {
      extra.innerHTML += '<div class="go-meta-titulo">DOSSIÊS EM ANDAMENTO</div>';
      const pastas = document.createElement('div');
      pastas.className = 'dossies-row';
      renderDossies(pastas, me.dossies);
      extra.appendChild(pastas);
    }

    this.renderUnlocks(data);
    this.renderSantinho(data, youId);

    // REELEIÇÃO: o clique pede o rematch; quem fecha a tela é o broadcast
    // game:rematch do servidor (a sala inteira volta pro comitê junta)
    const rematch = this.q('btn-rematch') as HTMLButtonElement;
    rematch.disabled = false;
    rematch.textContent = 'REELEIÇÃO';
    this.q('go-rematch-error').textContent = '';
    rematch.onclick = () => void this.pedirReeleicao(rematch, handlers);
    this.q('btn-again').onclick = handlers.onExit;

    this.root.style.display = '';
  }

  private async pedirReeleicao(btn: HTMLButtonElement, handlers: GameOverHandlers): Promise<void> {
    btn.disabled = true;
    btn.textContent = 'CONVOCANDO A CHAPA...';
    const err = await handlers.onRematch();
    if (err) this.disableRematch(err);
  }

  // A sala morreu (cartório arquivou / servidor reiniciou): sem reeleição
  disableRematch(msg: string): void {
    const btn = this.q('btn-rematch') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'REELEIÇÃO';
    this.q('go-rematch-error').textContent = msg;
  }

  // Reconectou com a sala ainda 'ended' (#91): o botão volta a valer — um
  // pedido de REELEIÇÃO pendente pode ter morrido junto com o socket
  enableRematch(): void {
    const btn = this.q('btn-rematch') as HTMLButtonElement;
    btn.disabled = false;
    btn.textContent = 'REELEIÇÃO';
    this.q('go-rematch-error').textContent = '';
  }

  // Quase-acerto VERDADEIRO (#91): NN% = tempo sobrevivido / 20min reais.
  // A barra anima do zero até o % real — morrer aos 17min dói de propósito.
  private renderApuracao(data: GameOverPayload): void {
    const boxEl = this.q('go-apuracao');
    if (data.victory) {
      boxEl.style.display = 'none';
      return;
    }
    boxEl.style.display = '';
    const fill = this.q('go-apuracao-fill');
    const pct = Math.min(100, Math.floor((data.timeSurvived / GAME_DURATION) * 100));
    if (data.infinite) {
      // No infinito a apuração já tinha sido 100% vencida: aqui o mérito é o extra
      const extra = Math.max(0, data.timeSurvived - GAME_DURATION);
      this.q('go-apuracao-pct').textContent = '100% CONCLUÍDA';
      this.q('go-apuracao-falta').textContent = `MODO INFINITO: aguentou ${fmt(extra)} além do fim da apuração.`;
      fill.classList.add('cheio');
    } else {
      const falta = Math.max(0, GAME_DURATION - data.timeSurvived);
      this.q('go-apuracao-pct').textContent = `${pct}% CONCLUÍDA`;
      this.q('go-apuracao-falta').textContent = `Caiu a ${fmt(falta)} do fim.`;
      fill.classList.remove('cheio');
    }
    fill.style.width = '0%';
    // dois rAFs: garante o layout com width 0 antes da transição começar
    requestAnimationFrame(() => requestAnimationFrame(() => {
      fill.style.width = `${data.infinite ? 100 : pct}%`;
    }));
  }

  // Ponto de extensão (issue #91 x meta-progressão #90): quando o perfil
  // persistido existir, as barras de desbloqueio animam aqui — os votos da
  // partida entrando no total vitalício, com o próximo desbloqueio subindo.
  private renderUnlocks(_data: GameOverPayload): void {
    this.q('go-unlocks').style.display = 'none';
  }

  // Santinho compartilhável (#96): sempre o do jogador LOCAL, com o título
  // honorífico decidido na comparação intra-chapa
  private renderSantinho(data: GameOverPayload, youId: string | null): void {
    const container = this.q('go-santinho');
    const you = data.stats.find((s) => s.id === youId);
    if (!you) {
      container.innerHTML = '';
      return;
    }
    const venceu = data.victory || data.infinite;
    const pct = Math.min(100, Math.floor((data.timeSurvived / GAME_DURATION) * 100));
    this.santinho.render(container, {
      name: you.name,
      charId: you.charId,
      kills: you.kills,
      level: you.level,
      timeSurvived: data.timeSurvived,
      titulo: honorifico(you, data.stats),
      resultado: venceu ? '100% VENCIDA' : `${pct}% CONCLUÍDA`,
      resultadoBom: venceu,
    });
  }

  visible(): boolean {
    return this.root.style.display !== 'none';
  }

  hide(): void {
    this.root.style.display = 'none';
  }
}
